import { usersService } from '@/services/users.service'
import {usersManagerService} from '@/services/usersManager.service'
import { authorizationsService } from '@/services/authorizations.service'
import { userParametersService } from '@/services/userParameters.service'
import { router } from '@/router'
import {companyParametersService} from "../../services/companyParameters.service";
import { COMPANY_LAYOUT_PARAM, DEFAULT_LAYOUT } from '@/helpers/companyLayouts'
import { EXTRA_FILTERS_PARAM, resolveExtraFilters } from '@/helpers/extraFilters'
import i18n from '@/i18n'

const state = {
  user: JSON.parse(localStorage.getItem('user')) || null,
  userParameters: {},
  userAuthorizations: JSON.parse(localStorage.getItem('userAuthorizations')) || [],
  isAdmin: localStorage.getItem('isAdmin') ? Boolean(JSON.parse(localStorage.getItem('isAdmin'))) : false,
  isSeaOperator: localStorage.getItem('isSeaOperator') ? Boolean(JSON.parse(localStorage.getItem('isSeaOperator'))) : false,
  internalVisibilityAuth: JSON.parse(localStorage.getItem('internalVisibilityAuth')) || null,
  houseVisibilityAuth: JSON.parse(localStorage.getItem('houseVisibilityAuth')) || null,
  filtersAreOpen: false,
  companyParameters: {
    nameMode: localStorage.getItem('nameMode') || 'full_name',
    extraFilters: localStorage.getItem('extraFilters'),
  },
  companyLayout: localStorage.getItem('companyLayout') || null,
}

const getters = {
  enabledExtraFilters(state) {
    return resolveExtraFilters(state.companyParameters.extraFilters, state.userAuthorizations)
  },
}

const actions = {
  async login({ dispatch, commit }, azureUser) {
    authorizationsService.getUserAuthorizations()
    .then((auths) => {
      commit('setUserAuthorizations', auths)
      commit('setInternalVisibilityAuth', auths.includes('AUTH_DISPLAY_INTERNAL_DATA'))
    })

    let user
    try {
      user = await usersService.findByAzureAccountOrEmail(azureUser.accountIdentifier, azureUser.userName)
    } catch (error) {
      let newUser = {
        id: null,
        username: azureUser.userName,
        email: azureUser.userName,
        firstName: azureUser.name.split(' ')[0],
        lastName: azureUser.name.split(' ')[1],
        azureAccountId: azureUser.accountIdentifier,
      }
      router.push({ name: 'newUserLanding' })
      user = await usersService.createUserFirstAccess(newUser)
    }

    commit('loginSuccess', user)
    commit('setAdminStatus', user.groups.some(g => g.groupDes === 'GR_ADMINISTRATORS'))

    if (!user.azureAccountId) {
      user.azureAccountId = azureUser.accountIdentifier
      usersService.updateUser(user.id, user).then((resp) => (user = resp))
    }

    return userParametersService
      .getUserParameter(user.id, 'LANGUAGE')
      .then((lang) => {
        if (lang.length == 0) {
          let userParam = {
            id: null,
            userId: user.id,
            key: 'LANGUAGE',
            valueType: 'STRING',
            value: 'en',
          }
          commit('setLanguage', userParam)
          userParametersService.createUserParameter(userParam).then((res) => {
            commit('setLanguage', res)
          })
        } else commit('setLanguage', lang[0])
      })
  },
  async loginCircle({commit, dispatch}, {username, password}) {
    const response = await usersManagerService.login(username, password);
    if (response) {
      const auths = await authorizationsService.getUserAuthorizations();
      commit('setUserAuthorizations', auths);
      localStorage.setItem('userAuthorizations', JSON.stringify(auths))

      commit('setInternalVisibilityAuth', auths.includes('AUTH_DISPLAY_INTERNAL_DATA'));
      localStorage.setItem('internalVisibilityAuth', auths.includes('AUTH_DISPLAY_INTERNAL_DATA'))

      commit('setHouseVisibilityAuth', auths.includes('AUTH_HOUSE_VISIBILITY'));
      localStorage.setItem('houseVisibilityAuth', auths.includes('AUTH_HOUSE_VISIBILITY'))

      const user = await usersManagerService.getByUsername(username);

      if (user) {
        commit('loginSuccess', user);
        localStorage.setItem('user', JSON.stringify(user))
        const isAdmin = user.groups.some(g => g.groupDes === 'GR_ADMINISTRATORS');
        commit('setAdminStatus', isAdmin);
        localStorage.setItem('isAdmin', JSON.stringify(isAdmin))

        const isSeaOperator = user.groups.some(g => g.groupDes === 'GR_SEA_OPERATORS');
        commit('setSeaOperatorStatus', isSeaOperator);
        localStorage.setItem('isSeaOperator', JSON.stringify(isSeaOperator))

        const lang = await userParametersService.getUserParameter(user.id, 'LANGUAGE');
        if (lang.length === 0) {
          const userParam = {
            id: null,
            userId: user.id,
            key: 'LANGUAGE',
            valueType: 'STRING',
            value: 'en',
          };
          const res = await userParametersService.createUserParameter(userParam);
          commit('setLanguage', res);

          i18n.locale = res.value;
        } else {
          commit('setLanguage', lang[0]);
          i18n.locale = lang[0].value;
        }

        const userNameMode = await companyParametersService.getByNameAndCompanyId(user.externalCompany.id, 'USER_NAME_MODE');

        if (userNameMode) {
          commit('setNameMode', userNameMode.value);
          localStorage.setItem('nameMode', userNameMode.value);
        } else {
          commit('setNameMode', 'full_name');
          localStorage.setItem('nameMode', 'full_name');
        }

        await dispatch('loadCompanyLayout', user.externalCompany.id);
        await dispatch('loadExtraFilters', user.externalCompany.id);

        router.push('/app');
      } else {
        console.error('User not found');
      }
    }
  },
  async loadCompanyLayout({ commit }, companyId) {
    let layout = DEFAULT_LAYOUT
    try {
      const param = await companyParametersService.getByNameAndCompanyId(companyId, COMPANY_LAYOUT_PARAM)
      if (param && param.value && param.value.trim()) layout = param.value.trim()
    } catch (error) {
      layout = DEFAULT_LAYOUT
    }
    commit('setCompanyLayout', layout)
    localStorage.setItem('companyLayout', layout)
  },
  async loadExtraFilters({ commit }, companyId) {
    let extraFilters = ''
    try {
      const param = await companyParametersService.getByNameAndCompanyId(companyId, EXTRA_FILTERS_PARAM)
      if (param && param.value) extraFilters = param.value
    } catch (error) {
      extraFilters = ''
    }
    commit('setExtraFilters', extraFilters)
    localStorage.setItem('extraFilters', extraFilters)
  },
}

const mutations = {
  loginSuccess(state, user) {
    state.user = user
  },
  setAdminStatus(state, isAdmin) {
    state.isAdmin = isAdmin
  },
  setSeaOperatorStatus(state, isSeaOperator) {
    state.isSeaOperator = isSeaOperator
  },
  setLanguage(state, language) {
    state.userParameters.language = language
  },
  setUserAuthorizations(state, auths){
    state.userAuthorizations = auths
  },
  setInternalVisibilityAuth(state, auth) {
    state.internalVisibilityAuth = auth
  },
  setHouseVisibilityAuth(state, auth) {
    state.houseVisibilityAuth = auth
  },
  setFiltersAreOpen(state, filtersAreOpen) {
    state.filtersAreOpen = filtersAreOpen
  },
  setNameMode(state, nameMode) {
    state.companyParameters.nameMode = nameMode
  },
  setCompanyLayout(state, companyLayout) {
    state.companyLayout = companyLayout
  },
  setExtraFilters(state, extraFilters) {
    state.companyParameters.extraFilters = extraFilters
  },
}

export const account = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
